<template>
  <div
    class="bg-surface-canvas dark:bg-surface-level-800 dark:ring-focus-level-700/60 dark:shadow-surface-scrim/30 max-w-3xl mx-auto overflow-hidden relative ring-1 ring-focus-level-200/70 rounded-3xl shadow-surface-level-900/5 shadow-xl"
  >
    <div class="p-6 sm:p-10">
      <div
        v-if="loading"
        class="animate-pulse flex flex-col items-center text-center"
      >
        <div class="bg-surface-level-200 dark:bg-surface-level-700 h-14 rounded-2xl w-14" />

        <div class="bg-surface-level-200 dark:bg-surface-level-700 h-7 mt-4 rounded w-48" />

        <div class="bg-surface-level-200 dark:bg-surface-level-700 h-4 mt-3 rounded w-64" />
      </div>

      <div
        v-else-if="error"
        class="py-6 text-center"
      >
        <div class="mb-3 text-5xl text-danger-500">
          {{ $t('commerce.brandSearchCard.warningSymbol') }}
        </div>

        <h2 class="dark:text-content-inverse font-bold mb-2 text-2xl text-content-level-900">
          {{ $t('commerce.brandSearchCard.brandNotFound') }}
        </h2>

        <p class="dark:text-content-level-400 mb-5 text-content-level-600">
          {{ error }}
        </p>

        <NuxtLink
          to="/brands"
          class="bg-primary cursor-pointer font-medium hover:bg-primary/90 inline-flex items-center justify-center px-5 py-2.5 rounded-xl text-content-inverse text-sm"
        >
          {{ $t('commerce.brandSearchCard.browseAllBrands') }}
        </NuxtLink>
      </div>

      <template v-else-if="brand">
        <div class="flex flex-col items-center text-center">
          <div
            class="bg-primary/10 flex font-bold h-14 items-center justify-center overflow-hidden ring-1 ring-primary/20 rounded-2xl text-2xl text-primary w-14"
          >
            <img
              v-if="brand.logo"
              :src="brand.logo"
              :alt="$t('commerce.brandSearchCard.logoAlt', { brandName: brand.name })"
              class="h-full object-cover w-full"
            />

            <span v-else>{{ brand.name.charAt(0) }}</span>
          </div>

          <h1
            class="dark:text-content-inverse font-semibold mt-4 sm:text-4xl text-3xl text-content-level-900 tracking-tight"
          >
            {{ brand.name }}
          </h1>

          <p
            v-if="brand.description"
            class="dark:text-content-level-400 max-w-xl mt-2 sm:text-base text-content-level-600 text-sm"
          >
            {{ brand.description }}
          </p>

          <p
            v-else
            class="dark:text-content-level-400 max-w-xl mt-2 sm:text-base text-content-level-600 text-sm"
          >
            {{ $t('commerce.brandSearchCard.browseTheStoresOfNameOnMealz', { name: brand.name }) }}
          </p>
        </div>

        <div class="mt-8">
          <AppForm
            class="flex flex-col gap-3 items-stretch sm:flex-row"
            @submit="handleSubmit"
          >
            <ClientOnly>
              <div class="flex-1 min-w-0">
                <div
                  v-show="showManualFallback"
                  class="brand-search-fallback"
                >
                  <TextField
                    :model-value="brandSearch.query.value"
                    type="text"
                    name="brand-search-query"
                    :placeholder="$t('commerce.brandSearchCard.addressSearchPlaceholder')"
                    @update:model-value="onSearchUpdate"
                  >
                    <template #label>
                      <span class="sr-only">{{
                        $t('commerce.brandSearchCard.searchStoresLabel')
                      }}</span>
                    </template>
                  </TextField>
                </div>

                <div
                  v-show="!showManualFallback"
                  :id="autocompleteId"
                  ref="autocompleteContainer"
                  class="bg-surface-canvas border border-border-level-300 brand-search-autocomplete dark:bg-surface-level-900 dark:border-border-level-600 dark:text-content-inverse focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 min-h-[52px] rounded-xl shadow-sm text-content-level-900 transition"
                />
              </div>
            </ClientOnly>

            <button
              type="submit"
              class="bg-primary cursor-pointer dark:focus:ring-offset-focus-level-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary font-medium hover:bg-primary/90 inline-flex items-center justify-center px-6 py-3 rounded-xl shadow-sm sm:text-base text-content-inverse text-sm"
              :disabled="
                brandSearch.isSearching.value ||
                brandSearch.isRedirecting.value ||
                !brandSearch.query.value.trim()
              "
            >
              <template v-if="brandSearch.isSearching.value">
                <svg
                  class="-ml-1 animate-spin h-4 mr-2 text-content-inverse w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />

                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  /></svg
                >{{ $t('commerce.brandSearchCard.searching') }}
              </template>

              <template v-else>
                {{ $t('commerce.brandSearchCard.searchStores') }}
              </template>
            </button>

            <a
              v-if="brand.website"
              :href="brand.website"
              target="_blank"
              rel="noopener noreferrer"
              class="bg-surface-canvas border border-border-level-300 cursor-pointer dark:bg-surface-level-800 dark:border-border-level-600 dark:focus:ring-offset-focus-level-800 dark:hover:bg-surface-level-700 dark:text-content-level-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary font-medium hover:bg-surface-level-50 inline-flex items-center justify-center px-6 py-3 rounded-xl shadow-sm sm:text-base text-content-level-700 text-sm"
            >
              {{ $t('commerce.brandSearchCard.visitWebsite') }}
            </a>
          </AppForm>

          <div
            v-if="brandSearch.submittedAddress.value && !brandSearch.isSearching.value"
            class="flex gap-2 items-center mt-3"
          >
            <span
              class="dark:text-content-level-400 max-w-xs text-content-level-500 text-xs truncate"
            >
              {{ brandSearch.submittedAddress.value }}
            </span>

            <button
              type="button"
              class="cursor-pointer dark:hover:text-content-level-300 hover:text-content-level-600 text-content-level-400 text-xs underline"
              @click="handleClearSearch"
            >
              {{ $t('commerce.brandSearchCard.clear') }}
            </button>
          </div>

          <p
            v-if="brandSearch.noResultsMessage.value"
            role="status"
            aria-live="polite"
            class="dark:text-content-level-400 mt-3 text-content-level-600 text-sm"
          >
            {{ brandSearch.noResultsMessage.value }}
          </p>

          <p
            v-if="brandSearch.error.value"
            role="alert"
            aria-live="assertive"
            class="mt-3 text-danger-text text-sm"
          >
            {{ brandSearch.error.value }}
          </p>
        </div>
      </template>
    </div>

    <div
      v-if="!loading && !error && brand"
      class="bg-[var(--brand-primary)] dark:bg-primary-800 relative text-content-inverse"
    >
      <div
        class="absolute bg-gradient-to-r from-surface-clear h-px inset-x-0 pointer-events-none to-surface-clear top-0 via-surface-canvas/20"
        aria-hidden="true"
      />

      <div class="px-6 py-7 sm:px-10 sm:py-9">
        <div
          class="dark:!text-content-inverse flex flex-wrap gap-x-2 gap-y-1 items-center justify-center text-center"
        >
          <span class="font-semibold sm:text-base text-sm">
            {{ $t('commerce.brandSearchCard.mealz') }}
          </span>

          <span
            class="opacity-40"
            aria-hidden="true"
            >|</span
          >

          <span class="opacity-90 sm:text-base text-sm">{{
            $t('commerce.brandSearchCard.whereGroupDiningGetsBooked')
          }}</span>
        </div>

        <div class="gap-x-10 gap-y-4 grid grid-cols-1 mt-6 sm:grid-cols-2">
          <div
            v-for="feature in features"
            :key="feature.title"
            class="flex gap-3 items-center"
          >
            <span
              class="bg-surface-canvas/15 flex h-9 items-center justify-center ring-1 ring-focus-inverse/10 rounded-lg shrink-0 text-content-inverse w-9"
            >
              <component
                :is="feature.icon"
                class="h-4 w-4"
              />
            </span>

            <p class="font-medium text-content-inverse/95 text-sm">
              {{ feature.title }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import AppForm from '~/design-system/molecules/AppForm.molecule.vue'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  toRef,
  unref,
  type Component,
  type Ref,
  useTemplateRef,
  watch
} from 'vue'
import { BadgeCheck, Bolt, PartyPopper, Users } from '@lucide/vue'
import TextField from '~/design-system/molecules/TextField.molecule.vue'
import type { AddressSelection } from '~/composables/AddressSelection'
import { useAddressAutocomplete } from '~/composables/useAddressAutocomplete'
import type { AutocompleteInstance, ProviderAddressResponse } from '~/lib/geo-providers/interfaces'
import { useLogger } from '~/composables/useLogger'
import { Normalization } from '~/utils/normalization'

const props = withDefaults(
  defineProps<{
    /** Indicates that the search request is in progress. */
    loading?: boolean
    /** Error message from the brand search request. */
    error?: string | null
    /** Brand data displayed by the card. */
    brand?: Record<string, unknown> | undefined
    /** Search callback data supplied to the card. */
    search?: Record<string, unknown> | undefined
  }>(),
  { brand: undefined, error: null, loading: false, search: undefined }
)

const emit = defineEmits<{ clearSearch: [] }>()

const logger = useLogger('BrandSearchCard')
const colorMode = useColorMode()

const isRecord = Normalization.isRecord

interface Brand {
  logo: string | null
  name: string
  description: string | null
  website: string | null
}

interface BrandSearch {
  query: Ref<string>
  isSearching: Ref<boolean>
  isRedirecting: Ref<boolean>
  submittedAddress: Ref<string>
  error: Ref<string | null>
  noResultsMessage: Ref<string | null>
  updateQuery: (_query: string) => void
  submit: () => Promise<void>
  applySelection: (_selection: AddressSelection) => void
}

interface Feature {
  title: string
  icon: Component
}

const getString = (value: unknown): string | null => (typeof value === 'string' ? value : null)

const getBrand = (value: unknown): Brand | null => {
  if (!isRecord(value)) {
    return null
  }

  const name = getString(value['name'])
  if (name === null || name.length === 0) {
    return null
  }

  return {
    description: getString(value['description']),
    logo: getString(value['logo']),
    name,
    website: getString(value['website'])
  }
}

const isBrandSearch = (value: unknown): value is BrandSearch => {
  if (!isRecord(value)) {
    return false
  }

  return (
    isRecord(value['query']) &&
    isRecord(value['isSearching']) &&
    isRecord(value['isRedirecting']) &&
    isRecord(value['submittedAddress']) &&
    isRecord(value['error']) &&
    isRecord(value['noResultsMessage']) &&
    typeof value['updateQuery'] === 'function' &&
    typeof value['submit'] === 'function' &&
    typeof value['applySelection'] === 'function'
  )
}

const fallbackSearch: BrandSearch = {
  applySelection: () => undefined,
  error: ref<null>(null),
  isRedirecting: ref<boolean>(false),
  isSearching: ref<boolean>(false),
  noResultsMessage: ref<null>(null),
  query: ref<string>(''),
  submit: async () => undefined,
  submittedAddress: ref<string>(''),
  updateQuery: () => undefined
}

const brand = computed(() => getBrand(props.brand))
const searchProp = toRef(props, 'search')
const searchValue = unref(searchProp)
const brandSearch = isBrandSearch(searchValue) ? searchValue : fallbackSearch
const features: Feature[] = [
  { icon: Users, title: 'Designed for Groups' },
  { icon: BadgeCheck, title: 'Verified Restaurants' },
  { icon: Bolt, title: 'Save Time and Money' },
  { icon: PartyPopper, title: 'For Every Occasion' }
]

const autocompleteId = 'brand-search-autocomplete'
const autocompleteContainer = useTemplateRef<HTMLElement>('autocompleteContainer')
const autocompleteInitialized = ref(false)
const showManualFallback = ref(true)

let autocompleteElement: HTMLElement | null = null
let autocompleteInputListener: ((_event: Event) => void) | null = null
let autocompleteInitToken = 0
let autocompleteInstance: AutocompleteInstance | null = null
let autocompleteClearListener: ((_event: CustomEvent<ProviderAddressResponse>) => void) | null =
  null

const isActiveAutocompleteSession = (token: number) => token === autocompleteInitToken

const cleanupAutocompleteInput = () => {
  autocompleteInitToken += 1

  if (autocompleteElement && autocompleteInputListener) {
    autocompleteElement.removeEventListener('input', autocompleteInputListener)
  }

  if (autocompleteInstance) {
    autocompleteInstance.destroy()
  }

  if (autocompleteContainer.value) {
    autocompleteContainer.value.innerHTML = ''
  }

  autocompleteElement = null
  autocompleteInputListener = null
  autocompleteInstance = null
  autocompleteClearListener = null
  autocompleteInitialized.value = false
  showManualFallback.value = true
}

const setAutocompleteInputValue = (value: string) => {
  autocompleteInstance?.setValue?.(value)
}

const onSearchUpdate = (value: string) => {
  brandSearch.updateQuery(value)
}

const handleClearSearch = () => {
  setAutocompleteInputValue('')
  emit('clearSearch')
}

const handleSubmit = async () => {
  await brandSearch.submit()

  if (!brandSearch.isRedirecting.value) {
    const el = document.getElementById('brand-stores')
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

const initializeAutocomplete = async () => {
  if (autocompleteInitialized.value || props.loading || !brand.value) return

  const token = autocompleteInitToken + 1
  autocompleteInitToken = token

  await nextTick()

  if (token !== autocompleteInitToken || props.loading || brand.value === null) return

  const container = document.getElementById(autocompleteId)
  if (!container) return

  container.innerHTML = ''

  if (token !== autocompleteInitToken) return

  try {
    const { initAutocomplete } = useAddressAutocomplete()
    const instance = await initAutocomplete(
      autocompleteId,
      async (selection) => {
        if (!isActiveAutocompleteSession(token)) return

        brandSearch.applySelection(selection)
        await handleSubmit()
      },
      'Search stores by address...',
      colorMode.value === 'dark' ? 'dark' : 'light'
    )

    if (!instance) {
      cleanupAutocompleteInput()
      return
    }

    // The widget may hide its input in a closed shadow root; its host element is the stable handle.
    const element = instance.getElement()
    if (token !== autocompleteInitToken || !element) {
      cleanupAutocompleteInput()
      return
    }

    autocompleteElement = element
    autocompleteInstance = instance
    instance.setAccessibleName('Search stores')
    setAutocompleteInputValue(brandSearch.query.value)

    autocompleteInputListener = () => {
      const value = instance.getValue()
      brandSearch.updateQuery(value)

      if (!value.trim()) {
        handleClearSearch()
      }
    }

    element.addEventListener('input', autocompleteInputListener)

    autocompleteClearListener = () => {
      if (!isActiveAutocompleteSession(token)) return

      handleClearSearch()
    }

    instance.addEventListener('clear', autocompleteClearListener)

    showManualFallback.value = false

    if (token === autocompleteInitToken) {
      autocompleteInitialized.value = true
    }
  } catch (e) {
    logger.error('initializeAutocomplete', { error: e })
    cleanupAutocompleteInput()
  }
}

watch(
  () => [props.loading, brand.value],
  async ([isLoading, currentBrand]) => {
    if (isLoading === true || currentBrand === null) {
      cleanupAutocompleteInput()
      return
    }

    await initializeAutocomplete()
  },
  { flush: 'post', immediate: true }
)

watch(
  () => colorMode.value,
  () => {
    cleanupAutocompleteInput()
    void initializeAutocomplete()
  }
)

onBeforeUnmount(() => {
  cleanupAutocompleteInput()
})

defineExpose({
  clearInput: () => {
    setAutocompleteInputValue('')
  }
})
</script>

<style scoped>
:deep(.brand-search-autocomplete) {
  width: 100%;
}

:deep(.brand-search-autocomplete .mapbox-search-box),
:deep(.brand-search-autocomplete .mapboxgl-ctrl-geocoder) {
  width: 100%;
  min-height: 52px;
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

:deep(.brand-search-autocomplete input[type='text']),
:deep(.brand-search-autocomplete .mapboxgl-ctrl-geocoder--input) {
  min-height: 52px;
  border: 0 !important;
  background: transparent !important;
  color: inherit;
  box-shadow: none !important;
  outline: none !important;
}

:deep(.brand-search-autocomplete input::placeholder) {
  color: rgb(156 163 175);
}
</style>
